import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, ViewChild, inject, signal, input, resource, effect } from "@angular/core";
import { JsonPipe, NgOptimizedImage, NgStyle } from "@angular/common";
import { Swiper } from "swiper";

import { register } from "swiper/element/bundle";
import { CaseStudiesService } from "../../data/case-studies.service";
import { RouterModule } from "@angular/router";
import { HostResizeDirective } from "../../host-width-change-listener.directive";
import { afterNextRender } from "@angular/core";
import { CaseStudySliderCardComponent } from "../case-study-slider-card/case-study-slider-card.component";
import { CardViewModel } from "../../models/card.vm";
import { Pagination } from "swiper/modules";

@Component({
    selector: "in2-case-studies-slider",
    standalone: true,
    imports: [NgStyle, NgOptimizedImage, RouterModule, CaseStudySliderCardComponent],
    templateUrl: "./case-studies-slider.component.html",
    styleUrls: ["./case-studies-slider.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        "[class]": "caseStudiesResource.value()?.length > 0 ? 'w-full' : 'hidden'",
    },
})
export class CaseStudiesSliderComponent extends HostResizeDirective implements OnDestroy {
    private readonly csService = inject(CaseStudiesService);

    aspectRatio = signal("53/75");

    @ViewChild("swiperContainer", { static: false }) swiperContainer: ElementRef | undefined;
    slides!: HTMLElement[];
    swiper!: Swiper;
    exclude = input<string[] | undefined, string | string[] | undefined>(undefined, {
        transform: (v) => {
            const arr = Array.isArray(v) ? v : v?.split(",").map((s) => s.trim());
            if (arr?.length) {
                return arr.filter((s) => s.length > 0);
            }
            return undefined;
        },
    });

    caseStudiesResource = resource({
        request: () => {
            return { exclude: this.exclude() };
        },
        loader: async ({ request }) => {
            return this.csService
                .list(request.exclude)
                .then((res) => res.data)
                .then((res) => {
                    return res.map(
                        (c) =>
                            ({
                                img: { src: c.image?.[0]?.path, alt: c.title, width: 500, height: 500 },
                                title: c.title,
                                slug: c.slug,
                                link: `/case-studies/${c.slug}`,
                                description: c.description,
                                meta: {},
                            }) as CardViewModel,
                    );
                });
        },
    });

    constructor() {
        super();
        afterNextRender(() => {
            register();
            this.initSwiper();
            this.widthChange.subscribe((w) => {
                let _w = 50;
                let _h = 70;
                if (w <= 640) {
                    _w = 1;
                    _h = 1;
                }
                const ratio = `${_w}/${_h}`;

                this._aspectRatio = ratio;
                this.aspectRatio.set(ratio);
                this.swiper?.update();
                this.swiperContainer?.nativeElement.click();
            });
        });
        this.debounce = 250;

        effect(() => {
            const value = this.caseStudiesResource.value();
            if (!value || value.length === 0) return;
        });
    }

    _aspectRatio = "53/75";

    override ngOnDestroy(): void {
        this.swiper?.destroy();
        super.ngOnDestroy();
    }

    loaders = new Map<string, Promise<any>>();

    private async initSwiper() {
        await new Promise((r) => setTimeout(r, 500));
        if (!this.swiperContainer) return;
        if (this.swiper) return this.swiper.update();
        try {
            this.swiper = new Swiper(this.swiperContainer.nativeElement, swiperConfig as any);
        } catch (error) {
            console.error(error);
        }
    }
}

const swiperConfig = {
    modules: [Pagination],
    direction: "horizontal",
    spaceBetween: 20,
    slidesPerView: "auto",
    freeMode: false,
    grabCurser: true,
    keyboard: {
        enabled: true,
        onlyInViewport: true,
    },
    // autoPlay: true,
    loop: false,
    breakpoints: {
        300: {
            slidesPerView: 1.25,
            pagination: {
                el: ".swiper-pagination",
            },
        },
        768: {
            slidesPerView: 2.25,
            pagination: {
                el: ".swiper-pagination",
            },
        },
        1180: {
            slidesPerView: 3.25,
        },
        // 1600: {
        //   slidesPerView: 4.25,
        //   spaceBetween: 40,
        // },
    },
    navigation: {
        nextEl: ".swiper-button-next",
        prevEl: ".swiper-button-prev",
    },
};
